import cx from 'classnames';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ButtonVariants } from '@components/button/Button';
import { CheckoutReceiptDetails } from '@components/checkout/CheckoutReceiptDetails';
import { Divider } from '@components/divider/Divider';
import { LinkStyledAsButton } from '@components/linkStyledAsButton/LinkStyledAsButton';
import { useDocumentTitle } from '@shared/hooks/setDocumentTitle';
import { ReservationNote } from 'webReservations/checkout/ReservationNote';
import { Address } from 'webReservations/reservations/Address';
import { ReservationAttributesGrid } from 'webReservations/reservations/ReservationAttributesGrid';
import linkStyles from '~styles/links.scss';
import typography from '~styles/typography.scss';
import { useRestaurantContext } from '../context/RestaurantContext';
import { RESERVATIONS_ROOT_PATH, TERMS_OF_SERVICE_PATH } from '../paths';
import { selectedAvailabilityDisplayName } from '../utils/selectedAvailabilityDisplayName';
import { CancellationPolicyNotice } from './CancellationPolicyNotice';
import styles from './CheckoutConfirmationPage.scss';
import { sendGa4FinishReservationEvent } from './gaHelpers';

export const CheckoutConfirmationPage = () => {
  const {
    state: {
      reservationId,
      date,
      lineItems,
      fee,
      tax,
      total,
      time,
      guestCount,
      purchasePrice,
      listingPublicName,
      isBestAvailable,
      note,
    },
  } = useLocation();
  const { restaurantDetails } = useRestaurantContext();

  useEffect(() => {
    if (!reservationId) return;
    sendGa4FinishReservationEvent({
      date,
      guestCount,
      price: purchasePrice,
      restaurantName: restaurantDetails.name,
      publicName: listingPublicName,
      time,
    });
  }, []);

  useDocumentTitle(`Checkout Reservation Confirmation | Peak Reservations`);

  return (
    <main className={styles.main}>
      <div className={styles.header}>
        <h1 className={typography.d3}>Thank you for using Peak!</h1>
        <p className={typography.c2}>
          You will receive a text or email confirming your booking soon.
        </p>
      </div>
      <div className={styles.reservation}>
        <section className={styles.card}>
          <p className={cx(typography.d4, styles.restaurantName)}>
            {restaurantDetails.name}
          </p>
          <Address value={restaurantDetails.address} />
          <ReservationAttributesGrid
            containerClassName={styles.reservationAttributesContainer}
            date={date}
            guestCount={guestCount}
            publicName={selectedAvailabilityDisplayName({
              publicName: listingPublicName,
              isBestAvailable,
            })}
            time={time}
          />
          <Divider />

          <CheckoutReceiptDetails
            className={styles.checkoutReceipt}
            lineItems={lineItems}
            fee={fee}
            hideZeroFee
            tax={tax}
            total={total}
            hideTaxesAndFees={total === 0}
          />

          <Link
            className={cx(linkStyles.linkPrimary, styles.refundPolicyLink)}
            to={`${TERMS_OF_SERVICE_PATH}#cancellations-and-refunds`}
            target="_blank"
          >
            Refund Policy
          </Link>
          <CancellationPolicyNotice
            date={date}
            purchasePrice={purchasePrice}
            availabilityTime={time}
          />
        </section>
        <section className={styles.card}>
          <ReservationNote note={note} reservationId={reservationId} />
        </section>
      </div>
      <div className={styles.actions}>
        <LinkStyledAsButton
          to={RESERVATIONS_ROOT_PATH}
          variant={ButtonVariants.Tertiary}
          label="View Your Reservations"
        />
      </div>
    </main>
  );
};
